import type {
    TenantMinimalAPI,
    TenantResponseAPI,
    IntegrationAccountEnvironments,
    TenantRegistrationResponseAPI,
    TenantRequestAPI,
} from '../types/Tenant';
import { fetchAndParse, uploadFile, fetchAndParseBlob } from '../utils/ajax';
import { formatTenantName, getTenantId } from '../utils/tenant';

export const getSubtenants = async () =>
    fetchAndParse<TenantResponseAPI[]>({
        url: '/api/admin/1/tenant/subtenants',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

export const provisionTenant = async (
    email: string,
    tenantName: string,
    firstName: string,
    lastName: string,
) =>
    fetchAndParse<TenantRegistrationResponseAPI>({
        url: '/api/admin/1/provisioning',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: { tenantName, email, firstName, lastName },
    });

export const createSubtenant = async (tenantName: string) =>
    fetchAndParse<TenantMinimalAPI>({
        url: '/api/admin/1/tenant/subtenants',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ManyWhoTenant: getTenantId(),
        },
        body: { developerName: tenantName, developerSummary: '' },
    });

export const getCurrentTenant = async () =>
    fetchAndParse<TenantResponseAPI>({
        url: '/api/admin/1/tenant',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

export const updateCurrentTenant = async (request: TenantRequestAPI) =>
    fetchAndParse<TenantResponseAPI>({
        url: '/api/admin/1/tenant',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ManyWhoTenant: getTenantId(),
        },
        body: request,
    });

export const exportTenant = async () =>
    fetchAndParseBlob({
        url: '/api/package/2/tenant',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

export const getExportTenantFileName = (tenantName: string) => {
    const formattedTenantName = formatTenantName(tenantName);
    const now = new Date();
    const year = now.getFullYear().toString();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const date = now.getDate().toString().padStart(2, '0');
    const hour = now.getHours().toString().padStart(2, '0');
    const minute = now.getMinutes().toString().padStart(2, '0');
    const second = now.getSeconds().toString().padStart(2, '0');
    const dateTimeStamp = `${year}${month}${date}${hour}${minute}${second}`;

    return `${formattedTenantName}_${dateTimeStamp}.tenant2`;
};

export const importTenant = ({
    file,
    progressHandler,
    completeHandler,
    errorHandler,
}: {
    file: File;
    progressHandler: (
        this: XMLHttpRequestUpload,
        ev: ProgressEvent<XMLHttpRequestEventTarget>,
    ) => unknown;
    completeHandler: (
        this: XMLHttpRequest,
        ev: ProgressEvent<XMLHttpRequestEventTarget>,
    ) => unknown;
    errorHandler: (
        this: XMLHttpRequest,
        ev: ProgressEvent<XMLHttpRequestEventTarget> | Event,
    ) => unknown;
}) =>
    uploadFile({
        url: '/api/package/1/tenant',
        sendAsJSON: true,
        file,
        progressHandler,
        completeHandler,
        errorHandler,
    });

export const allowed = async (tenantId: string) =>
    fetchAndParse<void>({
        url: '/api/draw/1/tenant/allowed',
        headers: {
            ManyWhoTenant: tenantId,
        },
    });

export const getAllIntegrationAccounts = (): Promise<string[]> =>
    fetchAndParse<string[]>({
        url: '/api/admin/1/tenant/integrationaccount',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

export const addIntegrationAccount = async (username: string, accountId: string, apiKey: string) =>
    fetchAndParse<void>({
        url: '/api/admin/1/tenant/integrationaccount',
        method: 'POST',
        headers: {
            ManyWhoTenant: getTenantId(),
            'Content-Type': 'application/json',
        },
        body: { username, accountId, apiKey },
    });

export const validateIntegrationAccount = async (
    username: string,
    accountId: string,
    apiKey: string,
) =>
    fetchAndParse<boolean>({
        url: '/api/admin/1/tenant/integrationaccount/validate',
        method: 'POST',
        headers: {
            ManyWhoTenant: getTenantId(),
            'Content-Type': 'application/json',
        },
        body: { username, accountId, apiKey },
    });

export const getAllIntegrationAccountEnvironments =
    async (): Promise<IntegrationAccountEnvironments> =>
        fetchAndParse<IntegrationAccountEnvironments>({
            url: '/api/admin/1/tenant/integrationaccount/environments',
            method: 'GET',
            headers: {
                ManyWhoTenant: getTenantId(),
            },
        });
